import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Users, Globe, Church } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const About = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO title="About Us — Our Mission to Deliver Your Prayer to the Holy Land" description="Learn how Jerusalem Holy Prayer physically delivers your prayer request to the holiest sites in Jerusalem — the Western Wall, Church of the Holy Sepulchre, Via Dolorosa — with photo proof of every delivery." path="/about" />
      {/* Hero Section */}
      <div 
        className="relative h-96 mb-16"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1587297069400-6cc5da81658a)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/60" />
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center">
          <div className="max-w-3xl text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/95 backdrop-blur-md rounded-full border-2 border-white shadow-2xl overflow-hidden">
                <img 
                  src="/logo-circle.png"
                  alt="Jerusalem Holy Prayer"
                  className="h-14 w-14 object-cover"
                />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-200">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Our Story */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">{t('aboutPage.ourStory')}</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p>{t('aboutPage.ourStoryP1')}</p>
            <p>{t('aboutPage.ourStoryP2')}</p>
            <p>{t('aboutPage.ourStoryP3')}</p>
            <p>{t('aboutPage.ourStoryP4')}</p>
            <p>{t('aboutPage.ourStoryP5')}</p>
            <p>{t('aboutPage.ourStoryP6')}</p>
            <p>{t('aboutPage.ourStoryP7')}</p>
          </div>
        </div>

        {/* Our Values */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">{t('aboutPage.ourValues')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: t('aboutPage.valueReverence'),
                description: t('aboutPage.valueReverenceDesc')
              },
              {
                icon: Users,
                title: t('aboutPage.valueCommunity'),
                description: t('aboutPage.valueCommunityDesc')
              },
              {
                icon: Globe,
                title: t('aboutPage.valueAccessibility'),
                description: t('aboutPage.valueAccessibilityDesc')
              },
              {
                icon: Church,
                title: t('aboutPage.valueAuthenticity'),
                description: t('aboutPage.valueAuthenticityDesc')
              }
            ].map((value, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all transform hover:scale-105">
                <CardContent className="p-8">
                  <div className="inline-block p-4 bg-amber-100 rounded-full mb-4">
                    <value.icon className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Our Messengers */}
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">{t('aboutPage.ourTeamTitle')}</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>{t('aboutPage.ourTeamP1')}</p>
                <p>{t('aboutPage.ourTeamP2')}</p>
                <p>{t('aboutPage.ourTeamP3')}</p>
                <p>{t('aboutPage.ourTeamP4')}</p>
                <p>{t('aboutPage.ourTeamP5')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;